import type { Inventory } from '.';
import { State } from 'src/replicant/State';
import { duration } from 'src/replicant/utils/duration';

export const inventoryGetters = (Inventory: Pick<Inventory, 'ruleset'>) => (
  state: Inventory.State,
) => ({
  /**
   * Get common currency
   */
  getPoints: () => getPoints(state),

  /**
   * Get premium currency
   */
  getGems: () => {
    return getFreeGems(state) + getPaidGems(state);
  },

  /**
   * Get current energy amount at given time
   */
  getEnergy: (now: number) => getEnergy(state, Inventory.ruleset, now),

  /**
   * Get time until energy is fully recharged.
   * @param now - Current time
   * @returns
   */
  getTimeToFullEnergy: (now: number) => {
    const {
      ruleset: { maxEnergy },
    } = Inventory;

    const { time } = getEnergyRegenerationConfig();

    const timeSinceLastEnergy =
      (now - state.inventory.energyRechargeStartTime) % time;
    const timeToNextEnergy = time - timeSinceLastEnergy;

    const amountToRefill =
      maxEnergy - getEnergy(state, Inventory.ruleset, now) - 1;
    const timeToFull = time * amountToRefill;

    return Math.max(timeToNextEnergy + timeToFull, 0);
  },

  getCurrencies: (now: number) => ({
    points: getPoints(state),
    energy: getEnergy(state, Inventory.ruleset, now),
    freeGems: getFreeGems(state),
    paidGems: getPaidGems(state),
  }),

  /**
   * Energy regeneration rate
   */
  getEnergyRegenerationConfig: () => getEnergyRegenerationConfig(),
});

const getPoints = ({ inventory }: Inventory.State) => inventory.points;
const getFreeGems = ({ inventory }: Inventory.State) => inventory.premium.free;
const getPaidGems = ({ inventory }: Inventory.State) => inventory.premium.paid;
const getEnergy = (
  state: Inventory.State & { ruleset: State['ruleset'] },
  ruleset: Inventory['ruleset'],
  now: number,
) => {
  // Get current state.
  const { energy, energyRechargeStartTime } = state.inventory;

  // Check if we have more energy then max.
  if (energy >= ruleset.maxEnergy) {
    // We may have gotten extra energy from somewhere. Don't lose it.
    return energy;
  }

  // How much time has passed since last energy regeneration.
  const duration = now - energyRechargeStartTime;

  // Get regeneration config.
  const { time, amount } = getEnergyRegenerationConfig();

  // How much energy should be regenerated.
  const regenerated = amount * Math.floor(duration / time);

  // Return current energy.
  return Math.min(regenerated + energy, ruleset.maxEnergy);
};

const getEnergyRegenerationConfig = () => {
  return { amount: 1, time: duration({ minutes: 45 }) };
};
